import { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../components/Button";
import { Input } from "../components/Input";
import { confirmPasswordReset } from "../services/api";

export default function ResetPassword() {
  const { t } = useTranslation();
  const { search } = useLocation();

  const token = useMemo(() => new URLSearchParams(search).get("token") || "", [search]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const hasToken = Boolean(token);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      return setError(t("reset_password.error_password"));
    }
    if (password !== confirm) {
      return setError(t("reset_password.error_mismatch"));
    }

    setLoading(true);
    try {
      await confirmPasswordReset({ token, password, password_confirm: confirm });
      setSuccess(true);
    } catch (err) {
      const code = err?.message || "";
      if (code === "token_expired") {
        setError(t("reset_password.error_token_expired"));
      } else if (code === "token_used") {
        setError(t("reset_password.error_token_used"));
      } else if (code === "token_invalid") {
        setError(t("reset_password.error_token_invalid"));
      } else if (code === "weak_password") {
        setError(t("reset_password.error_weak_password"));
      } else if (code === "password_mismatch") {
        setError(t("reset_password.error_mismatch"));
      } else {
        setError(t("reset_password.error_generic"));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-lg p-8 text-center">
        <img
          src="/favicon.png"
          alt="Zero Riscuri Logo"
          className="mx-auto h-24 w-24 object-contain mb-4"
        />

        <h1 className="text-2xl font-bold text-slate-900 mb-1">
          {t("reset_password.title")}
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          {t("reset_password.subtitle")}
        </p>

        {!hasToken ? (
          <div className="space-y-3 text-left">
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              {t("reset_password.missing_token")}
            </div>
            <div className="text-center pt-1">
              <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800 underline">
                {t("reset_password.back_to_login")}
              </Link>
            </div>
          </div>
        ) : success ? (
          <div className="space-y-4">
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              {t("reset_password.success_message")}
            </div>
            <Link
              to="/login"
              className="block text-sm text-blue-600 hover:text-blue-800 underline mt-2"
            >
              {t("reset_password.back_to_login")}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4 text-left">
            <div>
              <label className="block text-xs text-slate-500 font-medium mb-1">
                {t("reset_password.password_label")}
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="block text-xs text-slate-500 font-medium mb-1">
                {t("reset_password.confirm_label")}
              </label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                {error}
              </div>
            )}

            <Button disabled={loading} className="w-full justify-center">
              {loading
                ? t("reset_password.btn_loading")
                : t("reset_password.btn_submit")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
